import { useEffect } from 'react';
import type { Comic } from '@/mocks/comics';

interface ComicModalProps {
  comic: Comic | null;
  onClose: () => void;
}

export default function ComicModal({ comic, onClose }: ComicModalProps) {
  useEffect(() => {
    if (!comic) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [comic, onClose]);

  if (!comic) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`${comic.title}详情`}>
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-2xl bg-background-50 shadow-2xl">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-56 shrink-0 relative">
            <img
              src={comic.cover}
              alt={`${comic.title}高清韩国漫画封面`}
              className="w-full sm:h-full object-cover object-top aspect-[2/3] sm:aspect-auto"
            />
            <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-primary-500 text-xs font-bold text-background-50">
              {comic.rating}
            </span>
          </div>
          <div className="flex-1 p-5 md:p-7">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950">{comic.title}</h3>
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-background-300 cursor-pointer shrink-0"
                aria-label="关闭详情"
              >
                <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
              </button>
            </div>
            <p className="mt-2 text-xs text-foreground-500">
              {comic.author} · {comic.category} · {comic.status} · 共 {comic.chapterCount} 话 · {comic.views}阅读
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">{comic.description}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button type="button" className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap">
                <i className="ri-play-circle-line mr-1.5 w-4 h-4 inline-flex items-center justify-center" />
                开始阅读
              </button>
              <button type="button" className="px-5 py-2.5 rounded-lg bg-background-200 text-foreground-800 text-sm font-semibold hover:bg-background-300 cursor-pointer whitespace-nowrap">
                <i className="ri-bookmark-line mr-1.5 w-4 h-4 inline-flex items-center justify-center" />
                加入书架
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}